import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExplorePage } from "./ExplorePage";
import { supabase } from "@/integrations/supabase/client";
import { Session, User } from "@supabase/supabase-js";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { DashboardDataProvider } from "@/context/DashboardDataContext";
import { usePageMeta } from "@/hooks/usePageMeta";

type ExploreCategory = {
  id: string;
  name: string;
  slug: string | null;
  accentColor: string | null;
};

type ExploreItem = {
  id: string;
  title: string;
  description: string | null;
  author: string | null;
  coverUrl: string | null;
  accentColor: string | null;
  categoryId: string | null;
  url: string | null;
  isFeatured: boolean;
};

export default function ExplorePageContainer() {
  usePageMeta("Explore — Shattara AI", "", { noIndex: true });
  const [categories, setCategories] = useState<ExploreCategory[]>([]);
  const [items, setItems] = useState<ExploreItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (event === "SIGNED_OUT" || !session) {
        navigate("/auth/signin");
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (!session) {
        navigate("/auth/signin");
      }

      setAuthLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const [categoriesRes, itemsRes] = await Promise.all([
        supabase
          .from("explore_content_categories")
          .select("id, name, slug, accent_color")
          .order("order_index", { ascending: true }),
        supabase
          .from("explore_content")
          .select(
            "id, title, description, author, cover_url, accent_color, category_id, url, is_featured"
          )
          .order("order_index", { ascending: true }),
      ]);

      if (categoriesRes.error) throw categoriesRes.error;
      if (itemsRes.error) throw itemsRes.error;

      const mappedCategories: ExploreCategory[] =
        categoriesRes.data?.map((c: any) => ({
          id: String(c.id),
          name: c.name,
          slug: c.slug ?? null,
          accentColor: c.accent_color ?? null,
        })) ?? [];

      const mappedItems: ExploreItem[] =
        itemsRes.data?.map((item: any) => ({
          id: String(item.id),
          title: item.title,
          description: item.description ?? null,
          author: item.author ?? null,
          coverUrl: item.cover_url ?? null,
          accentColor: item.accent_color ?? null,
          categoryId: item.category_id ? String(item.category_id) : null,
          url: item.url ?? null,
          isFeatured: item.is_featured ?? false,
        })) ?? [];

      setCategories(mappedCategories);
      setItems(mappedItems);
    } catch (err: any) {
      console.error("[ExplorePage] fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user, fetchData]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!session || !user) {
    return null;
  }

  return (
    <DashboardDataProvider>
      <DashboardShell>
        <main className="flex-1 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto w-full py-8">
            <ExplorePage
              categories={categories}
              items={items}
              isLoading={loading}
            />
          </div>
        </main>
      </DashboardShell>
    </DashboardDataProvider>
  );
}
